import React from "react";
import { Box, Tooltip, Typography, styled } from "@mui/material";
import type { MSHatData, RegionSalaryData, SimpleNamedItem } from "../../types";

interface BackRightPanelProps {
  hat: MSHatData;
  selectedRegionSalary?: RegionSalaryData;
  nationalSalary?: RegionSalaryData;
}

const PanelContainer = styled(Box, {
  label: "BackRightPanelContainer",
})(() => ({
  width: 368,
  flexShrink: 0,
  height: 569,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  overflowY: "auto",
  backgroundColor: "rgba(33, 43, 54, 0.85)",
  borderRadius: 16,
  padding: "20px 24px",
  border: "1px solid rgba(145, 158, 171, 0.16)",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(145, 158, 171, 0.3)",
    borderRadius: "3px",
  },
}));

const SectionHeader = styled(Typography, {
  label: "BackRightSectionHeader",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "18px",
  lineHeight: "28px",
  color: "#ffffff",
}));

const SubSectionHeader = styled(Typography, {
  label: "BackRightSubSectionHeader",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "11px",
  lineHeight: "16px",
  color: "rgba(255, 255, 255, 0.64)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
}));

const ColumnHeader = styled(Typography, {
  label: "BackRightColumnHeader",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "13px",
  lineHeight: "20px",
  color: "rgba(255, 255, 255, 0.64)",
}));

const SalaryAmount = styled(Typography, {
  label: "BackRightSalaryAmount",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "20px",
  lineHeight: "30px",
  color: "#ffffff",
}));

const ItemRow = styled(Box, {
  label: "BackRightItemRow",
})(() => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  paddingTop: 4,
  paddingBottom: 4,
}));

const ItemIcon = styled(Box, {
  label: "BackRightItemIcon",
})(() => ({
  width: 28,
  height: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}));

const ItemName = styled(Typography, {
  label: "BackRightItemName",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 500,
  fontSize: "13px",
  lineHeight: "20px",
  color: "#ffffff",
}));

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle
      cx="8"
      cy="8"
      r="7"
      stroke="rgba(255,255,255,0.48)"
      strokeWidth="1.5"
    />
    <path
      d="M8 7v4M8 5h.01"
      stroke="rgba(255,255,255,0.48)"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const GreenDot = () => (
  <svg width="12" height="12" viewBox="0 0 12 12">
    <circle cx="6" cy="6" r="5" fill="#36B37E" />
  </svg>
);

const PurpleFlag = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M3 1v12M3 1h8l-2 3 2 3H3"
      fill="#9B59B6"
      stroke="#9B59B6"
      strokeWidth="1"
      strokeLinejoin="round"
    />
  </svg>
);

const hasItems = (items?: SimpleNamedItem[]): boolean =>
  !!items && items.length > 0;

const formatSalary = (value?: number): string => {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

const NamedItemList = ({ items }: { items?: SimpleNamedItem[] }) => {
  if (!items || items.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {items.map((item) => (
        <ItemRow key={item.id}>
          {item.icon?.url && (
            <ItemIcon>
              <img
                src={item.icon.url}
                alt=""
                width={24}
                height={24}
                style={{ objectFit: "contain" }}
              />
            </ItemIcon>
          )}
          <ItemName>{item.name}</ItemName>
        </ItemRow>
      ))}
    </Box>
  );
};

export const BackRightPanel = ({
  hat,
  selectedRegionSalary,
  nationalSalary,
}: BackRightPanelProps) => {
  return (
    <PanelContainer>
      {/* Average Salary */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <SectionHeader>Average Salary</SectionHeader>
        <Tooltip title="Salary data is based on regional averages and may vary">
          <Box sx={{ cursor: "pointer", display: "flex" }}>
            <InfoIcon />
          </Box>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", gap: 4 }}>
        <Box sx={{ flex: 1, textAlign: "center" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              mb: 0.5,
            }}
          >
            <GreenDot />
            <ColumnHeader>Your Region</ColumnHeader>
          </Box>
          <SalaryAmount>
            {formatSalary(selectedRegionSalary?.payRange?.mean)}
          </SalaryAmount>
        </Box>
        <Box sx={{ flex: 1, textAlign: "center" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              mb: 0.5,
            }}
          >
            <PurpleFlag />
            <ColumnHeader>National</ColumnHeader>
          </Box>
          <SalaryAmount>
            {formatSalary(nationalSalary?.payRange?.mean)}
          </SalaryAmount>
        </Box>
      </Box>

      {/* Education - only show if any sub-sections have data */}
      {(hasItems(hat.msConnectedCourses) ||
        hasItems(hat.msKeysToSuccess) ||
        hasItems(hat.msPopularPaths) ||
        hasItems(hat.msOtherRoutes)) && (
        <SectionHeader>Education</SectionHeader>
      )}

      {/* High School Priorities - only show if either column has data */}
      {(hasItems(hat.msConnectedCourses) || hasItems(hat.msKeysToSuccess)) && (
        <>
          <SubSectionHeader>High School Priorities</SubSectionHeader>
          <Box sx={{ display: "flex", gap: 3 }}>
            {hasItems(hat.msConnectedCourses) && (
              <Box sx={{ flex: 1 }}>
                <ColumnHeader sx={{ mb: 1 }}>Connected Courses</ColumnHeader>
                <NamedItemList items={hat.msConnectedCourses} />
              </Box>
            )}
            {hasItems(hat.msKeysToSuccess) && (
              <Box sx={{ flex: 1 }}>
                <ColumnHeader sx={{ mb: 1 }}>Keys to Success</ColumnHeader>
                <NamedItemList items={hat.msKeysToSuccess} />
              </Box>
            )}
          </Box>
        </>
      )}

      {/* After High School - only show if either column has data */}
      {(hasItems(hat.msPopularPaths) || hasItems(hat.msOtherRoutes)) && (
        <>
          <SubSectionHeader>After High School</SubSectionHeader>
          <Box sx={{ display: "flex", gap: 3 }}>
            {hasItems(hat.msPopularPaths) && (
              <Box sx={{ flex: 1 }}>
                <ColumnHeader sx={{ mb: 1 }}>Popular Paths</ColumnHeader>
                <NamedItemList items={hat.msPopularPaths} />
              </Box>
            )}
            {hasItems(hat.msOtherRoutes) && (
              <Box sx={{ flex: 1 }}>
                <ColumnHeader sx={{ mb: 1 }}>Other Routes</ColumnHeader>
                <NamedItemList items={hat.msOtherRoutes} />
              </Box>
            )}
          </Box>
        </>
      )}
    </PanelContainer>
  );
};
